import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStatusInfo } from '../utils/orderStatus';
import DeliveryMap from '../components/DeliveryMap';
import { useRoute } from '../hooks/useRoute';

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { route, loading: routeLoading, error: routeError } = useRoute(id);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await fetch(`/api/orders/${id}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!response.ok) throw new Error('Order not found');
        const data = await response.json();
        setOrder(data);

        let vendorId = null;
        if (data.hubId) {
          vendorId = data.hubId._id || data.hubId;
        } else if (data.producer) {
          vendorId = data.producer._id || data.producer;
        } else if (data.products && data.products.length > 0) {
          vendorId = data.products[0]?.producerId;
        }

        if (vendorId) {
          const vendorRes = await fetch(`/api/users/${vendorId}`);
          const vendorData = await vendorRes.json();
          setVendor(vendorData);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  // Get dynamic status message
  const getStatusMessage = (order, pickupName, sellerName) => {
    if (order.status === 'delivered') {
      return {
        message: `Your order has been delivered!`,
        detail: `Delivered at ${new Date(order.statusHistory?.find(h => h.status === 'delivered')?.changedAt || order.createdAt).toLocaleTimeString()}. Thank you for shopping with Our Pickup!`,
        icon: '✅'
      };
    }
    if (order.status === 'in_transit') {
      return {
        message: `${pickupName || 'Delivery partner'} has picked up your order`,
        detail: `Picked up at ${new Date(order.statusHistory?.find(h => h.status === 'in_transit')?.changedAt || new Date()).toLocaleTimeString()}. It's on its way to you!`,
        icon: '🚚'
      };
    }
    if (order.status === 'claimed') {
      return {
        message: `${pickupName || 'Delivery partner'} is on their way to pick up your order`,
        detail: `They are heading to ${sellerName} to collect your order.`,
        icon: '🚗'
      };
    }
    if (order.readyForPickup) {
      return {
        message: `Your order is ready for pickup!`,
        detail: `${sellerName} has prepared your order. Waiting for a delivery partner to claim it.`,
        icon: '✅'
      };
    }
    return {
      message: `Your order is being prepared`,
      detail: `${sellerName} is getting your order ready. We'll notify you when it's ready for pickup.`,
      icon: '⏳'
    };
  };

  // Calculate progress steps
  const getProgressSteps = (order) => {
    const steps = [
      { key: 'placed', label: 'Order Placed', icon: '📦', completed: true },
      { key: 'claimed', label: 'Pickup Assigned', icon: '🚗', completed: order.status === 'claimed' || order.status === 'in_transit' || order.status === 'delivered' },
      { key: 'ready', label: 'Ready for Pickup', icon: '🏪', completed: order.readyForPickup },
      { key: 'transit', label: 'In Transit', icon: '🚚', completed: order.status === 'in_transit' || order.status === 'delivered' },
      { key: 'delivered', label: 'Delivered', icon: '🏠', completed: order.status === 'delivered' }
    ];
    return steps;
  };

  if (loading) return <div className="text-center py-8">Loading order confirmation...</div>;
  if (error) return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  if (!order) return <div className="text-center py-8">Order not found</div>;

  let vendorName = 'Vendor';
  if (order.hubId) vendorName = order.hubId.name || 'Hub';
  else if (order.producer) vendorName = order.producer.farmName || order.producer.name || 'Producer';
  else if (vendor) vendorName = vendor.farmName || vendor.name;

  const pickupName = order.assignedPickupId?.name || 'Delivery partner';
  const statusMessage = getStatusMessage(order, pickupName, vendorName);
  const progressSteps = getProgressSteps(order);
  const statusInfo = getStatusInfo(order.status);
  const formatDate = (dateString) => new Date(dateString).toLocaleString();

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Order Confirmation</h1>
      <p className="text-gray-500 mb-6">Order #{order._id.slice(-8).toUpperCase()}</p>

      {/* Progress Timeline */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center">
          {progressSteps.map((step, idx) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center text-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                  step.completed 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-200 text-gray-400'
                }`}>
                  {step.icon}
                </div>
                <span className={`text-xs mt-2 ${step.completed ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>
                  {step.label}
                </span>
              </div>
              {idx < progressSteps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step.completed && progressSteps[idx + 1]?.completed ? 'bg-green-600' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Status Message Card */}
      <div className={`bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 ${statusInfo.color.replace('text', 'border')}`}>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl">{statusMessage.icon}</span>
          <h2 className="text-xl font-semibold">{statusMessage.message}</h2>
        </div>
        <p className="text-gray-600">{statusMessage.detail}</p>
        {order.readyForPickupAt && (
          <p className="text-xs text-gray-400 mt-2">Ready for pickup since: {formatDate(order.readyForPickupAt)}</p>
        )}
      </div>

      {/* Delivery Map (only for claimed, in_transit, delivered) */}
      {(order.status === 'claimed' || order.status === 'in_transit' || order.status === 'delivered') && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Delivery Tracking</h2>
          {routeLoading ? (
            <div className="text-center py-8">Loading route...</div>
          ) : routeError ? (
            <div className="text-center py-8 text-red-500">Unable to load delivery route</div>
          ) : route ? (
            <DeliveryMap
              pickup={route.pickup}
              delivery={route.delivery}
              route={route.route}
              height={350}
            />
          ) : (
            <div className="text-center py-8 text-gray-500">Route information not available</div>
          )}
        </div>
      )}

      {/* Order Details */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Order Details</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Order Date</p>
            <p>{formatDate(order.createdAt)}</p>
          </div>
          <div>
            <p className="text-gray-500">Seller</p>
            <p>{vendorName}</p>
          </div>
          <div>
            <p className="text-gray-500">Buyer</p>
            <p>{order.buyer?.name}</p>
          </div>
          <div>
            <p className="text-gray-500">Payment Method</p>
            <p className="capitalize">{order.paymentMethod?.replace('_', ' ')}</p>
          </div>
          {order.assignedPickupId?.name && (
            <div>
              <p className="text-gray-500">Delivery Partner</p>
              <p>{order.assignedPickupId.name}</p>
            </div>
          )}
        </div>
      </div>

      {/* Products */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Items Ordered</h2>
        <div className="space-y-3">
          {order.products.map((item, idx) => {
            const imageUrl = item.product?.images?.[0] ? `/uploads/${item.product.images[0]}` : null;
//            const imageUrl = item.images && item.images[0] ? `/uploads/${item.images[0]}` : null;
            return (
                <div key={idx} className="flex items-center gap-4 border-b pb-3">
                  {item.product?.images?.[0] ? (
                  <img 
                    src={`/uploads/${item.product.images[0]}`} 
                    alt={item.name}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                ) : (
                  <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs">
                    No img
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-sm text-gray-500">Quantity: {item.quantity} × ${item.price}/{item.unit}</p>
                </div>
                <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-2 border-t">
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span>
            <span>${order.totalAmount.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Delivery Address */}
      {order.deliveryAddress && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Delivery Address</h2>
          <p>{order.deliveryAddress.address}</p>
          <p>{order.deliveryAddress.suburb}, {order.deliveryAddress.state} {order.deliveryAddress.postcode}</p>
        </div>
      )}

      {/* Delivery Notes */}
      {order.notes && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Delivery Notes</h2>
          <p className="text-gray-600">{order.notes}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between gap-4">
        <Link to="/orders" className="bg-gray-600 text-white px-6 py-2 rounded hover:bg-gray-700 text-center">
          ← Back to Orders
        </Link>
        <Link to="/products" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 text-center">
          Continue Shopping →
        </Link>
      </div>
    </div>
  );
}
